import { useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce';
import {
    ComposedChart,
    Bar,
    Area,
    XAxis,
    YAxis,
    Brush,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
    ResponsiveContainer,
} from 'recharts'

const getGradientOffset = (data) => {
    const dataMax = Math.max(...data.map((i) => i.net));
    const dataMin = Math.min(...data.map((i) => i.net));
  
    if (dataMax <= 0) {
      return 0;
    }
    if (dataMin >= 0) {
      return 1;
    }
  
    return dataMax / (dataMax - dataMin);
};

const monthTickFormatter = (tick) => {
    const date = new Date(tick)

    return date.getMonth() + 1
}

const renderYearTick = (tickProps) => {
    const { x, y, payload } = tickProps
    const { value } = payload
    const month = value.getMonth()
    const year = value.getFullYear()
    const showYearTick = month === 0

    return showYearTick ? (
        <text x={x} y={y} textAnchor="middle">{`${year}`}</text>
    ) : null
}

const NetIncomeChart = ({ data }) => {
    const [isDesktop, setIsDesktop] = useState(window.matchMedia('(min-width: 768px)').matches)
    const processedData = data.map((item) => {
        return {
            ...item,
            income: parseFloat(item.income),
            expenses: parseFloat(item.expenses),
            net: parseFloat(item.net),
        }
    })

    const gradientOffset = getGradientOffset(processedData);
    const keys = ['income', 'expenses', 'net']
    const min = getMinNegativeRounded(processedData, keys)
    const max = getMaxPositiveRounded(processedData, keys)
    
    const responsiveAspect = isDesktop ? 2.25 : 1
    
    const listener = useDebouncedCallback(
        () => setIsDesktop(window.matchMedia('(min-width: 768px)').matches),
        300
    );

    useEffect(() => {
        window.addEventListener('resize', listener)
        window.addEventListener('DOMContentLoaded', listener)

        return () => {
            window.removeEventListener('resize', listener)
            window.removeEventListener('DOMContentLoaded', listener)
        }
    }, [])

    return (
        <ResponsiveContainer aspect={responsiveAspect}>
            <ComposedChart
                data={processedData}
                margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                }}
                stackOffset="sign"
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="date"
                    tickFormatter={monthTickFormatter}
                    strokeWidth={1}
                    xAxisId={0}
                />
                <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    interval={0}
                    tick={renderYearTick}
                    height={1}
                    xAxisId={1}
                />
                <YAxis domain={[min, max]} type="number" />
                <Tooltip
                    labelFormatter={(value) => (
                        `${(value.getMonth() + 1).toString().padStart(2, '0')}/${value.getFullYear()}`
                    )}
                    formatter={(value, name) => 
                        [
                            parseFloat(value).toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                            }),
                            name.charAt(0).toUpperCase() + name.slice(1),
                        ]
                    }
                 />
                <Legend
                    wrapperStyle={{
                        paddingTop: 15,
                        paddingBottom: 5,
                    }}
                    formatter={(value) => value.charAt(0).toUpperCase() + value.slice(1)}
                />
                <Brush dataKey="displayMonth" height={30} stroke="#8884d8" travellerWidth={10} />
                <Bar stackId="stack" dataKey="income" fill="rgba(0,0,0,.6)" />
                <Bar stackId="stack" dataKey="expenses" fill="rgba(230,32,14,.6)" />
                <defs>
                    <linearGradient id="splitColor" x1="0" y1="0" x2="0" y2="1">
                        <stop offset={gradientOffset} stopColor="#000000" stopOpacity={1} />
                        <stop offset={gradientOffset} stopColor="#e6200e" stopOpacity={1} />
                    </linearGradient>
                </defs>
                <Area type="monotone" dataKey="net" stroke="#000" fill="url(#splitColor)" />
                <ReferenceLine y={0} stroke="#000" isFront={true} />
            </ComposedChart>
        </ResponsiveContainer>
    )
}

const reduceByKey = (data, key, getMin = false) => {
    return data.reduce((prev, curr) => {
        if (getMin) {
            return prev[key] < curr[key] ? prev : curr
        }
        return prev[key] > curr[key] ? prev : curr
    })[key]
}

const getMaxPositiveRounded = (data, keys) => {
    const max = keys
        .map((key) => reduceByKey(data, key))
        .reduce((a, b) => Math.max(a, b))

    const rounded = Math.ceil(max / 1000) * 1000

    return rounded
}

const getMinNegativeRounded = (data, keys) => {
    const min = keys
        .map((key) => reduceByKey(data, key, true))
        .reduce((a, b) => Math.min(a, b))

    const rounded = Math.floor(min / 1000) * 1000

    return rounded
}

export default NetIncomeChart
